import React, { useEffect, useState } from 'react';
import { Swords, Crown, Trophy } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials, formatNumber } from '../../lib/utils';

/**
 * BattlePanel — shows the active live battle for a stream:
 *   - Both hosts' avatars
 *   - Coin race bar (host1 vs host2 share of total coins)
 *   - Countdown timer
 *   - Winner banner when battle ends
 */
export default function BattlePanel({ battle, onEnded }) {
  const [secondsLeft, setSecondsLeft] = useState(battle?.seconds_remaining || 0);

  useEffect(() => {
    if (!battle || battle.status !== 'active') return;
    setSecondsLeft(battle.seconds_remaining || 0);
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          onEnded?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [battle, onEnded]);

  if (!battle) return null;

  const total = (battle.host1_coins || 0) + (battle.host2_coins || 0);
  const h1Pct = total === 0 ? 50 : Math.round((battle.host1_coins / total) * 100);
  const h2Pct = 100 - h1Pct;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  const isEnded = battle.status === 'ended';
  const winnerId = battle.winner_id;
  const winnerName = winnerId
    ? (winnerId === battle.host1_id
        ? (battle.host1_display_name || battle.host1_username)
        : (battle.host2_display_name || battle.host2_username))
    : null;

  return (
    <div
      data-testid="battle-panel"
      className="absolute top-16 left-1/2 -translate-x-1/2 z-20 w-[92%] max-w-md
                 bg-gradient-to-r from-pink-500/95 via-purple-500/95 to-indigo-500/95
                 backdrop-blur-md rounded-2xl p-3 shadow-2xl ring-2 ring-white/30"
    >
      <div className="flex items-center justify-center gap-2 mb-2">
        <Swords className="w-4 h-4 text-white" />
        <span className="text-white text-xs font-bold tracking-wide uppercase">
          {isEnded ? 'Battle Ended' : 'Live Battle'}
        </span>
        {!isEnded && (
          <span className="text-white text-sm font-mono font-bold ml-2" data-testid="battle-timer">
            {mm}:{ss}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Host 1 */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Avatar className="w-8 h-8 ring-2 ring-yellow-300">
            <AvatarImage src={battle.host1_avatar} />
            <AvatarFallback className="text-xs bg-pink-700 text-white">
              {getInitials(battle.host1_display_name || battle.host1_username || 'A')}
            </AvatarFallback>
          </Avatar>
          <span className="text-white text-xs font-semibold truncate max-w-[60px]">
            @{battle.host1_username}
          </span>
        </div>

        {/* Coin race bar */}
        <div className="relative flex-1 h-6 rounded-full bg-black/40 overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-pink-400 to-pink-600"
            style={{ width: `${h1Pct}%`, transition: 'width 0.5s ease' }}
          />
          <div
            className="absolute right-0 top-0 h-full bg-gradient-to-l from-indigo-400 to-indigo-600"
            style={{ width: `${h2Pct}%`, transition: 'width 0.5s ease' }}
          />
          <div className="absolute inset-0 flex items-center justify-between px-2 text-white text-[11px] font-bold drop-shadow">
            <span data-testid="host1-coins">{formatNumber(battle.host1_coins || 0)}</span>
            <span data-testid="host2-coins">{formatNumber(battle.host2_coins || 0)}</span>
          </div>
        </div>

        {/* Host 2 */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-white text-xs font-semibold truncate max-w-[60px] text-right">
            @{battle.host2_username}
          </span>
          <Avatar className="w-8 h-8 ring-2 ring-cyan-300">
            <AvatarImage src={battle.host2_avatar} />
            <AvatarFallback className="text-xs bg-indigo-700 text-white">
              {getInitials(battle.host2_display_name || battle.host2_username || 'B')}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Winner banner */}
      {isEnded && (
        <div
          data-testid="battle-winner-banner"
          className="mt-3 flex items-center justify-center gap-2 bg-yellow-400/95 text-yellow-900 rounded-full px-3 py-1.5 font-bold"
        >
          {winnerId ? (
            <>
              <Crown className="w-4 h-4" />
              <span>Winner: @{winnerName}</span>
              <Trophy className="w-4 h-4" />
            </>
          ) : (
            <span>It's a draw!</span>
          )}
        </div>
      )}
    </div>
  );
}
